import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Search, Plus, Minus, Printer, Settings,
  Trash2, Package, Tag, Layers, RefreshCcw, ChevronsRight, X,
  ChevronDown, ChevronRight, AlertTriangle, ClipboardList
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Barcode from 'react-barcode';
import ReactDOMServer from 'react-dom/server';
import { sanitizeProductDisplayName, getVariantName, getCleanVariantLabel, buildProductDisplayName } from '../utils/productLabels';
import { getLocalDateKey } from '../utils/localDateTime';
import { applyActiveProductsFilter } from '../utils/productStatus';

const CONFIG_STORAGE_KEY = 'impulza:etiquetas-config';
const LOTE_STORAGE_KEY = 'impulza:etiquetas-lote';

const ROLL_PRESETS = [
  { id: '50x25', w: 50, h: 25, label: 'Rollo 50 × 25 mm' },
  { id: '40x25', w: 40, h: 25, label: 'Rollo 40 × 25 mm' },
  { id: '32x25', w: 32, h: 25, label: 'Rollo 32 × 25 mm' },
  { id: '58x40', w: 58, h: 40, label: 'Rollo 58 × 40 mm' },
  { id: '100x50', w: 100, h: 50, label: 'Rollo 100 × 50 mm' },
];

const DEFAULT_CONFIG = {
  mostrarNombre: true,
  mostrarPrecio: true,
  mostrarBarcode: true,
  mostrarSKU: false,
  fontSize: 'normal', // small, normal, large
  labelSize: 'A4', // A4 (grilla) o ROLL (rollo continuo)
  a4Width: 5,
  a4Height: 3,
  rollWidth: 50,
  rollHeight: 25,
};

const FONT_SIZES = {
  small: { name: 8, price: 12, sku: 6, campo: 7, caption: 5.5, barcodeFont: 8, barcodeW: 1, barcodeH: 25 },
  normal: { name: 10, price: 16, sku: 8, campo: 9, caption: 6.5, barcodeFont: 10, barcodeW: 1.2, barcodeH: 30 },
  large: { name: 12, price: 20, sku: 9, campo: 11, caption: 8, barcodeFont: 10, barcodeW: 1.2, barcodeH: 30 },
};

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]
));

// dd/mm/aaaa a partir de una fecha YYYY-MM-DD (sin pasar por Date para no comerse el timezone)
const formatFechaCorta = (ymd) => {
  if (!ymd || typeof ymd !== 'string' || ymd.length < 10) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

const compactFecha = (ymd) => (ymd && ymd.length >= 10 ? ymd.slice(2).replace(/-/g, '') : '');

const addDaysToYmd = (ymd, days) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const base = new Date(y, (m || 1) - 1, d || 1);
  base.setDate(base.getDate() + Number(days || 0));
  return getLocalDateKey(base);
};

const esLoteAutogenerado = (lote, fechaYmd) => {
  if (!lote) return true;
  const compact = compactFecha(fechaYmd);
  return !!compact && new RegExp(`^L${compact}-\\d{2}$`).test(lote);
};

const suggestLote = (fechaYmd) => {
  const compact = compactFecha(fechaYmd) || compactFecha(getLocalDateKey());
  let seq = 1;
  try {
    const raw = window.localStorage.getItem(`${LOTE_STORAGE_KEY}:${compact}`);
    seq = raw ? Number(raw) || 1 : 1;
  } catch { /* no-op */ }
  return `L${compact}-${String(seq).padStart(2, '0')}`;
};

const bumpLoteSeq = (fechaYmd) => {
  const compact = compactFecha(fechaYmd) || compactFecha(getLocalDateKey());
  let seq = 1;
  try {
    const raw = window.localStorage.getItem(`${LOTE_STORAGE_KEY}:${compact}`);
    seq = (raw ? Number(raw) || 1 : 1) + 1;
    window.localStorage.setItem(`${LOTE_STORAGE_KEY}:${compact}`, String(seq));
  } catch { /* no-op */ }
  return `L${compact}-${String(seq).padStart(2, '0')}`;
};

const loadStoredConfig = () => {
  try {
    const raw = window.localStorage.getItem(CONFIG_STORAGE_KEY);
    if (!raw) return DEFAULT_CONFIG;
    const parsed = JSON.parse(raw);
    const merged = { ...DEFAULT_CONFIG, ...(parsed || {}) };
    // Migración del valor viejo 'ROLL_50x25' al esquema con medidas configurables
    if (merged.labelSize === 'ROLL_50x25') {
      merged.labelSize = 'ROLL';
      merged.rollWidth = 50;
      merged.rollHeight = 25;
    }
    if (merged.labelSize !== 'A4' && merged.labelSize !== 'ROLL') merged.labelSize = 'A4';
    return merged;
  } catch {
    return DEFAULT_CONFIG;
  }
};

export default function InventarioEtiquetas({ onNavigate }) {
  const { profile } = useAuth();

  // States - Data
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);

  // States - Search
  const [busqueda, setBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [expandidos, setExpandidos] = useState({});

  // States - Queue
  // cart format: { key, product, variant, qty, peso } indexado por key (productId o productId::variantId)
  const [etiquetasQueue, setEtiquetasQueue] = useState({});
  const [qtyDrafts, setQtyDrafts] = useState({});

  // States - Tipo de etiqueta
  const [labelType, setLabelType] = useState('comercial'); // 'comercial' | 'elaboracion'

  // States - Config visual
  const [configParams, setConfigParams] = useState(loadStoredConfig);

  // States - Datos de tanda (etiqueta de elaboración)
  const [batchParams, setBatchParams] = useState(() => {
    const hoy = getLocalDateKey();
    return {
      fechaElaboracion: hoy,
      lote: suggestLote(hoy),
      elaboradoPor: '',
      mostrarVencimiento: false,
      vencimientoModo: 'dias', // 'dias' | 'fecha'
      vidaUtilDias: 3,
      fechaVencimiento: '',
      mostrarPeso: false,
      mostrarLoteBarcode: false,
    };
  });

  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printHtml, setPrintHtml] = useState('');
  const iframeRef = useRef(null);

  useEffect(() => {
    if (profile?.organization_id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Default de "Elaborado por" con el usuario logueado (sin pisar lo que el usuario haya escrito)
  useEffect(() => {
    const nombre = profile?.full_name || profile?.name || profile?.email || '';
    if (!nombre) return;
    setBatchParams((prev) => (prev.elaboradoPor ? prev : { ...prev, elaboradoPor: nombre }));
  }, [profile]);

  // Persistencia de la configuración visual
  useEffect(() => {
    try {
      window.localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(configParams));
    } catch { /* no-op */ }
  }, [configParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const orgId = profile.organization_id;

      // Talles y colores: la etiqueta lleva color / talle. Si la consulta
      // extendida falla (esquema no aplicado en esta base) se usa la clásica.
      const buildProductsQuery = (variantSelect) => applyActiveProductsFilter(
        supabase
        .from('products')
        .select(`id, name, barcode, sell_price, category_id, active, brand, stock_tracking, ${variantSelect}`)
        .eq('organization_id', orgId)
      ).order('name');
      const [resProdsExtendida, resCats] = await Promise.all([
        buildProductsQuery('product_variants(id, sku, barcode, price, attributes, talle, talle_orden, color_id, activo, archivado_at, colores(id, nombre))'),
        supabase
          .from('product_categories')
          .select('id, name')
          .eq('organization_id', orgId)
          .order('name')
      ]);

      let resProds = resProdsExtendida;
      if (resProds.error) {
        console.error('Etiquetas: catálogo con talles no disponible, uso el clásico:', resProds.error);
        resProds = await applyActiveProductsFilter(
          supabase
          .from('products')
          .select('id, name, barcode, sell_price, category_id, active, brand, product_variants(id, sku, barcode, price, attributes)')
          .eq('organization_id', orgId)
        ).order('name');
      }
      if (resProds.error) {
        console.error("Error fetching products:", resProds.error);
        throw resProds.error;
      }
      if (resCats.error) {
        console.error("Error fetching categories:", resCats.error);
        throw resCats.error;
      }

      // Defaulting to active=true frontend side in case the column is somehow problematic
      const activeProducts = (resProds.data || [])
        .filter(p => p.active !== false)
        .map(p => {
          // Variantes archivadas (p.ej. la "Base" de un producto que pasó a
          // talles) no se etiquetan.
          const variants = (Array.isArray(p.product_variants) ? p.product_variants : [])
            .filter((v) => !v?.archivado_at && (p.stock_tracking !== 'variant' || v?.activo !== false));
          return {
            ...p,
            variants,
            sku: variants[0]?.sku || null
          };
        });

      setProductos(activeProducts);
      setCategorias(resCats.data || []);
    } catch (error) {
      toast.error('Error al cargar catálogo de productos');
    } finally {
      setLoading(false);
    }
  };

  // Variantes que aportan información real (nombre propio, sku o barcode distinto)
  const variantesUtiles = (prod) => (prod.variants || []).filter(v => (
    getCleanVariantLabel(getVariantName(v)) || v.barcode || v.sku
  ));

  const tieneVariantesExpandibles = (prod) => {
    const utiles = variantesUtiles(prod);
    if (utiles.length > 1) return true;
    return utiles.length === 1 && !!getCleanVariantLabel(getVariantName(utiles[0]));
  };

  // Filtrado de la lista base (busca también dentro de las variantes)
  const productosFiltrados = productos.filter(p => {
    const term = busqueda.toLowerCase().trim();
    const safeName = p.name ? p.name.toLowerCase() : '';

    const matchVariante = (p.variants || []).some(v => (
      (v.sku && v.sku.toLowerCase().includes(term)) ||
      (v.barcode && v.barcode.toLowerCase().includes(term)) ||
      (getVariantName(v).toLowerCase().includes(term) && term.length > 0)
    ));

    const matchBusqueda = !term ||
                          safeName.includes(term) ||
                          (p.barcode && p.barcode.toLowerCase().includes(term)) ||
                          matchVariante;

    const matchCat = filtroCategoria === 'todas' || p.category_id === filtroCategoria;
    return matchBusqueda && matchCat;
  });

  // Resolución de los datos que efectivamente van impresos (variante pisa al producto padre)
  const resolveLabelData = (item) => {
    const { product, variant } = item;
    const name = buildProductDisplayName(product?.name, variant ? getVariantName(variant) : '');
    const barcode = variant?.barcode || product?.barcode || '';
    const sku = variant?.sku || product?.sku || '';
    const variantPrice = Number(variant?.price);
    const price = variant && Number.isFinite(variantPrice) && variantPrice > 0
      ? variantPrice
      : Number(product?.sell_price || 0);
    return { name, barcode, sku, price };
  };

  // Operaciones Queue
  const queueKey = (prodId, variantId) => (variantId ? `${prodId}::${variantId}` : String(prodId));

  const addToQueue = (prod, variant = null, qtyToAdd = 1) => {
    const key = queueKey(prod.id, variant?.id);
    setEtiquetasQueue(prev => {
      const currentQty = prev[key]?.qty || 0;
      return {
        ...prev,
        [key]: {
          key,
          product: prod,
          variant,
          peso: prev[key]?.peso || '',
          qty: currentQty + qtyToAdd
        }
      };
    });
  };

  const removeFromQueue = (key) => {
    setEtiquetasQueue(prev => {
      const clone = { ...prev };
      delete clone[key];
      return clone;
    });
  };

  const updateQueueQty = (key, newQty) => {
    const parsed = Number(newQty);
    const safeQty = Number.isFinite(parsed) ? Math.floor(parsed) : 0;
    if (safeQty <= 0) {
      removeFromQueue(key);
      return;
    }
    setEtiquetasQueue(prev => (prev[key] ? {
      ...prev,
      [key]: { ...prev[key], qty: Math.min(safeQty, 999) }
    } : prev));
  };

  // El input de cantidad usa un borrador de texto: borrar el campo para tipear otro número
  // no puede eliminar el ítem de la cola. El descarte solo ocurre si confirma un 0.
  const handleQtyInput = (key, rawValue) => {
    setQtyDrafts(prev => ({ ...prev, [key]: rawValue }));
    const parsed = Number(rawValue);
    if (rawValue !== '' && Number.isFinite(parsed) && parsed >= 1) {
      updateQueueQty(key, parsed);
    }
  };

  const commitQtyDraft = (key) => {
    const draft = qtyDrafts[key];
    setQtyDrafts(prev => {
      const clone = { ...prev };
      delete clone[key];
      return clone;
    });
    if (draft === undefined) return;
    const parsed = Number(draft);
    if (draft === '' || !Number.isFinite(parsed)) return; // se queda con la cantidad anterior
    if (parsed <= 0) removeFromQueue(key);
  };

  const updateQueuePeso = (key, peso) => {
    setEtiquetasQueue(prev => (prev[key] ? {
      ...prev,
      [key]: { ...prev[key], peso }
    } : prev));
  };

  const addAllFiltered = () => {
    if (productosFiltrados.length === 0) return;
    setEtiquetasQueue(prev => {
      const clone = { ...prev };
      productosFiltrados.forEach(p => {
        const key = queueKey(p.id, null);
        const currentQty = clone[key]?.qty || 0;
        clone[key] = {
          key,
          product: p,
          variant: null,
          peso: clone[key]?.peso || '',
          qty: currentQty === 0 ? 1 : currentQty
        };
      });
      return clone;
    });
    toast.success(`${productosFiltrados.length} productos añadidos a la cola`);
  };

  const addAllVariants = (prod) => {
    const utiles = variantesUtiles(prod);
    if (utiles.length === 0) return;
    setEtiquetasQueue(prev => {
      const clone = { ...prev };
      utiles.forEach(v => {
        const key = queueKey(prod.id, v.id);
        const currentQty = clone[key]?.qty || 0;
        clone[key] = {
          key,
          product: prod,
          variant: v,
          peso: clone[key]?.peso || '',
          qty: currentQty === 0 ? 1 : currentQty
        };
      });
      return clone;
    });
    toast.success(`${utiles.length} variantes añadidas a la cola`);
  };

  const clearQueue = () => {
    setEtiquetasQueue({});
    setQtyDrafts({});
  };

  const toggleExpandido = (prodId) => {
    setExpandidos(prev => ({ ...prev, [prodId]: !prev[prodId] }));
  };

  const queueItems = Object.values(etiquetasQueue);
  const totalEtiquetas = queueItems.reduce((acc, item) => acc + item.qty, 0);

  // Aviso: productos en cola sin código de barras (solo relevante en etiqueta comercial con barcode activo)
  const itemsSinBarcode = queueItems.filter(item => !resolveLabelData(item).barcode);

  // Fecha de vencimiento efectiva según el modo elegido
  const vencimientoEfectivo = batchParams.vencimientoModo === 'dias'
    ? addDaysToYmd(batchParams.fechaElaboracion, batchParams.vidaUtilDias)
    : batchParams.fechaVencimiento;

  // ── Generación del markup de impresión (CSS inline, sin dependencias externas) ──

  const renderBarcodeSvg = (value, fs) => {
    if (!value) return '';
    try {
      return ReactDOMServer.renderToStaticMarkup(
        <Barcode
          value={String(value)}
          format="CODE128"
          displayValue
          fontSize={fs.barcodeFont}
          width={fs.barcodeW}
          height={fs.barcodeH}
          margin={0}
          background="transparent"
        />
      );
    } catch {
      return '';
    }
  };

  const buildComercialLabelHtml = (item) => {
    const fs = FONT_SIZES[configParams.fontSize] || FONT_SIZES.normal;
    const { name, barcode, sku, price } = resolveLabelData(item);
    const safeName = (name || 'S/N').toUpperCase();
    const safePrice = Number(price || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 });

    const partes = [];

    if (configParams.mostrarNombre) {
      partes.push(`<div style="font-weight:900;font-size:${fs.name}px;line-height:1.15;letter-spacing:-0.02em;color:#1e293b;margin-bottom:1mm;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${escapeHtml(safeName)}</div>`);
    }

    if (configParams.mostrarPrecio) {
      partes.push(`<div style="font-weight:900;font-size:${fs.price}px;line-height:1;color:#0f172a;display:flex;align-items:flex-start;justify-content:center;"><span style="font-size:0.6em;position:relative;top:0.2em;margin-right:0.1em;">$</span>${escapeHtml(safePrice)}</div>`);
    }

    if (configParams.mostrarSKU && sku) {
      partes.push(`<div style="font-weight:700;font-size:${fs.sku}px;color:#64748b;text-transform:uppercase;letter-spacing:0.1em;margin-top:1mm;">REF: ${escapeHtml(sku)}</div>`);
    }

    if (configParams.mostrarBarcode && barcode) {
      partes.push(`<div style="margin-top:auto;width:100%;max-height:40px;display:flex;justify-content:center;padding-top:1mm;overflow:hidden;">${renderBarcodeSvg(barcode, fs)}</div>`);
    }

    return `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;width:100%;height:100%;padding:0.25rem;overflow:hidden;background:#fff;box-sizing:border-box;">${partes.join('')}</div>`;
  };

  const buildElaboracionLabelHtml = (item) => {
    const fs = FONT_SIZES[configParams.fontSize] || FONT_SIZES.normal;
    const { name } = resolveLabelData(item);
    const safeName = (name || 'S/N').toUpperCase();

    const campo = (etiqueta, valor) => `
      <div style="display:flex;align-items:baseline;gap:0.8mm;line-height:1.25;margin-top:0.6mm;">
        <span style="font-size:${fs.caption}px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.06em;white-space:nowrap;">${escapeHtml(etiqueta)}</span>
        <span style="font-size:${fs.campo}px;font-weight:900;color:#0f172a;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">${escapeHtml(valor)}</span>
      </div>`;

    const partes = [];
    partes.push(`<div style="font-weight:900;font-size:${fs.name}px;line-height:1.15;letter-spacing:-0.02em;color:#0f172a;text-transform:uppercase;border-bottom:0.4mm solid #0f172a;padding-bottom:0.6mm;margin-bottom:0.6mm;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${escapeHtml(safeName)}</div>`);

    partes.push(campo('Elab.:', formatFechaCorta(batchParams.fechaElaboracion)));

    if (batchParams.mostrarVencimiento && vencimientoEfectivo) {
      partes.push(campo('Vto.:', formatFechaCorta(vencimientoEfectivo)));
    }

    if (batchParams.lote) {
      partes.push(campo('Lote:', batchParams.lote));
    }

    if (batchParams.mostrarPeso && item.peso) {
      partes.push(campo('Cont. neto:', item.peso));
    }

    if (batchParams.elaboradoPor) {
      partes.push(campo('Elaborado por:', batchParams.elaboradoPor));
    }

    if (batchParams.mostrarLoteBarcode && batchParams.lote) {
      const payload = `${batchParams.lote}-${compactFecha(batchParams.fechaElaboracion)}`;
      partes.push(`<div style="margin-top:auto;width:100%;display:flex;justify-content:center;padding-top:1mm;overflow:hidden;">${renderBarcodeSvg(payload, fs)}</div>`);
    }

    return `<div style="display:flex;flex-direction:column;align-items:stretch;justify-content:flex-start;text-align:left;width:100%;height:100%;overflow:hidden;background:#fff;box-sizing:border-box;">${partes.join('')}</div>`;
  };

  const buildPrintHtml = () => {
      const isElaboracion = labelType === 'elaboracion';

      // 1. Renderizamos una sola vez por ítem de la cola y repetimos el string qty veces
      const cellsHtml = queueItems.map(item => {
          const inner = isElaboracion ? buildElaboracionLabelHtml(item) : buildComercialLabelHtml(item);
          const cell = `<div class="label-cell">${inner}</div>`;
          return cell.repeat(item.qty);
      }).join('');

      // 2. Estructura y CSS (Varía si es A4 o Rollo Continuo)
      const isA4 = configParams.labelSize === 'A4';

      let css = `
        @page { margin: 0; }
        body {
            margin: 0;
            padding: 0;
            background: white !important;
            font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        * { box-sizing: border-box; }
        .label-container { width: 100%; }
        .label-container::after { content: ""; display: table; clear: both; }
        .label-cell {
            page-break-inside: avoid;
            break-inside: avoid;
            overflow: hidden;
            float: left;
            display: flex;
            flex-direction: column;
        }
      `;

      if (isA4) {
          // Dynamic A4 Size calculation in CM
          const cellW = Math.max(1, configParams.a4Width || 5);
          const cellH = Math.max(1, configParams.a4Height || 3);

          css += `
            @page { size: A4 portrait; margin: 10mm 5mm; }
            .label-container {
                width: 200mm;
                margin: 0 auto;
            }
            .label-cell {
                width: ${cellW}cm;
                height: ${cellH}cm;
                border: 1px dashed #ccc; /* Guia de recorte opcional */
                padding: 1mm;
                /* El margen debe considerarse extra al tamaño de la etiqueta */
                margin-right: 2mm;
                margin-bottom: 2mm;
            }
          `;
      } else {
          const rollW = Math.max(10, Number(configParams.rollWidth) || 50);
          const rollH = Math.max(10, Number(configParams.rollHeight) || 25);
          css += `
            @page { size: ${rollW}mm ${rollH}mm !important; margin: 0 !important; }
            body { width: ${rollW}mm; margin: 0; padding: 0; }
            .label-container { width: ${rollW}mm; display: block; }
            .label-cell {
                width: ${rollW}mm;
                height: ${rollH}mm;
                padding: 1mm;
                border: none;
                float: none;
                page-break-after: always; /* Fuerzo salto por cada etiqueta en rollo */
            }
            /* Para que el último no haga un formfeed vacío */
            .label-cell:last-child { page-break-after: auto; }
          `;
      }

      // Sin CDN externo: todo el estilo va inline o en este <style>, así imprime igual sin internet
      return `
        <!DOCTYPE html>
        <html lang="es">
        <head>
            <meta charset="utf-8">
            <title>Etiquetas</title>
            <style>
                html { line-height: 1.5; -webkit-text-size-adjust: 100%; }
                ${css}
            </style>
        </head>
        <body>
            <div class="label-container">
                ${cellsHtml}
            </div>
            <script>
                window.onload = function() {
                    setTimeout(function() { window.print(); }, 150);
                };
            </script>
        </body>
        </html>
      `;
  };

  const validarImpresion = () => {
    if (totalEtiquetas === 0) {
      toast.error("No hay etiquetas en la cola para imprimir.");
      return false;
    }

    if (labelType === 'elaboracion') {
      if (!batchParams.fechaElaboracion) {
        toast.error("Cargá la fecha de elaboración.");
        return false;
      }
      if (batchParams.mostrarVencimiento) {
        if (!vencimientoEfectivo) {
          toast.error("Cargá la fecha de vencimiento o la vida útil en días.");
          return false;
        }
        if (vencimientoEfectivo < batchParams.fechaElaboracion) {
          toast.error("El vencimiento no puede ser anterior a la elaboración.");
          return false;
        }
      }
      if (batchParams.mostrarLoteBarcode && !batchParams.lote) {
        toast.error("Para imprimir el código del lote necesitás un número de lote.");
        return false;
      }
      if (!batchParams.lote) {
        toast('Vas a imprimir sin número de lote.', { icon: '⚠️' });
      }
    }

    return true;
  };

  const handlePrint = () => {
      if (!validarImpresion()) return;
      const html = buildPrintHtml();
      setPrintHtml(html);
      setPrintModalOpen(true);
  };

  const formatoActual = configParams.labelSize === 'A4'
    ? `Hoja A4 · ${configParams.a4Width}×${configParams.a4Height} cm`
    : `Rollo ${configParams.rollWidth}×${configParams.rollHeight} mm`;

  if (loading && productos.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Cargando Catálogo...</p>
      </div>
    );
  }

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500 relative">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5 px-1">
        <div className="flex items-center gap-3">
            <button
                onClick={() => onNavigate('inventario')}
                className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
            >
                <ArrowLeft size={18} />
            </button>
            <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
                <Printer size={18} className="text-[#3FF2AA]" />
            </div>
            <div>
                <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Impresión de Etiquetas</h1>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    Góndola y trazabilidad de elaboración
                </p>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* LEFT: MAIN SELECTOR */}
          <div className="lg:col-span-8 flex flex-col gap-5">

              {/* Toolbar */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                  <div className="flex flex-col md:flex-row gap-3 items-center">
                    <div className="relative w-full md:flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                        <input
                            type="text"
                            placeholder="Buscar producto a imprimir..."
                            className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                        />
                    </div>
                    <div className="w-full md:w-48 lg:w-64">
                         <select
                            value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
                        >
                            <option value="todas">TODAS LAS CATEGORÍAS</option>
                            {categorias.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <button
                        onClick={addAllFiltered}
                        className="w-full md:w-auto px-4 py-2.5 bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 rounded-xl transition-all shadow-sm text-[10px] font-black uppercase tracking-widest flex items-center justify-center whitespace-nowrap"
                    >
                        <ChevronsRight size={14} className="mr-1" /> Mover Filtrados
                    </button>
                  </div>
              </div>

              {/* Catálogo Listado */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col flex-1 h-[600px] overflow-hidden">
                  <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                          <Package size={14} className="text-white" />
                          <span className="text-[10px] font-black text-white uppercase tracking-widest">Catálogo de Productos ({productosFiltrados.length})</span>
                      </div>
                  </div>
                  <div className="overflow-y-auto flex-1">
                      {productosFiltrados.length === 0 ? (
                           <div className="p-10 text-center text-slate-400 text-[10px] font-black uppercase tracking-widest">No hay resultados</div>
                      ) : (
                          <div className="divide-y divide-slate-100">
                              {productosFiltrados.map(prod => {
                                  const keyBase = queueKey(prod.id, null);
                                  const qItem = etiquetasQueue[keyBase];
                                  const isSelected = !!qItem;
                                  const expandible = tieneVariantesExpandibles(prod);
                                  const abierto = !!expandidos[prod.id];
                                  const utiles = expandible ? variantesUtiles(prod) : [];
                                  return (
                                    <div key={prod.id} className={isSelected ? 'bg-blue-50/10' : ''}>
                                      <div className="flex items-center justify-between p-3 px-4 hover:bg-slate-50 transition-colors">
                                        <div className="flex items-center gap-2 pr-2 min-w-0">
                                            {expandible && (
                                                <button
                                                    onClick={() => toggleExpandido(prod.id)}
                                                    className="p-1 rounded-lg text-slate-400 hover:text-[#51A7FD] hover:bg-slate-100 transition-colors shrink-0"
                                                    title="Ver variantes"
                                                >
                                                    {abierto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                </button>
                                            )}
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-[11px] font-black text-slate-800 uppercase tracking-tight flex items-center gap-1.5 flex-wrap">
                                                    <span>{sanitizeProductDisplayName(prod.name)}</span>
                                                    {prod.brand && (
                                                        <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">
                                                            {prod.brand}
                                                        </span>
                                                    )}
                                                    {expandible && (
                                                        <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">
                                                            {utiles.length} variantes
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="text-[9px] font-bold text-slate-400 tracking-widest uppercase mt-0.5">
                                                    {prod.barcode ? `BAR: ${prod.barcode} • ` : prod.sku ? `REF: ${prod.sku} • ` : ''} $ {Number(prod.sell_price || 0).toLocaleString('es-AR')}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {isSelected ? (
                                                <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
                                                    <button onClick={() => updateQueueQty(keyBase, qItem.qty - 1)} className="w-6 h-6 flex items-center justify-center bg-slate-50 rounded text-slate-600 hover:bg-blue-100"><Minus size={12}/></button>
                                                    <span className="w-8 text-center text-[11px] font-black text-[#51A7FD]">{qItem.qty}</span>
                                                    <button onClick={() => updateQueueQty(keyBase, qItem.qty + 1)} className="w-6 h-6 flex items-center justify-center bg-slate-50 rounded text-slate-600 hover:bg-blue-100"><Plus size={12}/></button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => addToQueue(prod)}
                                                    className="px-4 py-2 bg-slate-50 border border-slate-200 text-slate-600 rounded-xl hover:bg-[#51A7FD] hover:text-white hover:border-[#51A7FD] transition-all text-[9.5px] font-black uppercase tracking-widest flex items-center gap-1 shadow-sm"
                                                >
                                                    Agregar
                                                </button>
                                            )}
                                        </div>
                                      </div>

                                      {/* Variantes */}
                                      {expandible && abierto && (
                                        <div className="bg-slate-50/70 border-t border-slate-100 px-4 py-2">
                                            <div className="flex justify-end mb-1">
                                                <button
                                                    onClick={() => addAllVariants(prod)}
                                                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-500 hover:text-[#51A7FD] hover:border-[#51A7FD] rounded-lg text-[8.5px] font-black uppercase tracking-widest transition-all"
                                                >
                                                    Agregar todas las variantes
                                                </button>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                {utiles.map(v => {
                                                    const kv = queueKey(prod.id, v.id);
                                                    const qv = etiquetasQueue[kv];
                                                    const nombreVar = getCleanVariantLabel(getVariantName(v)) || v.sku || 'Variante';
                                                    const precioVar = Number(v.price) > 0 ? Number(v.price) : Number(prod.sell_price || 0);
                                                    return (
                                                        <div key={v.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3 py-2">
                                                            <div className="flex flex-col min-w-0 pr-2">
                                                                <span className="text-[10px] font-black text-slate-700 uppercase tracking-tight truncate">{nombreVar}</span>
                                                                <span className="text-[8.5px] font-bold text-slate-400 tracking-widest uppercase mt-0.5 truncate">
                                                                    {v.barcode ? `BAR: ${v.barcode} • ` : v.sku ? `REF: ${v.sku} • ` : 'SIN CÓDIGO • '} $ {precioVar.toLocaleString('es-AR')}
                                                                </span>
                                                            </div>
                                                            {qv ? (
                                                                <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1 shrink-0">
                                                                    <button onClick={() => updateQueueQty(kv, qv.qty - 1)} className="w-6 h-6 flex items-center justify-center bg-white rounded text-slate-600 hover:bg-blue-100"><Minus size={12}/></button>
                                                                    <span className="w-8 text-center text-[11px] font-black text-[#51A7FD]">{qv.qty}</span>
                                                                    <button onClick={() => updateQueueQty(kv, qv.qty + 1)} className="w-6 h-6 flex items-center justify-center bg-white rounded text-slate-600 hover:bg-blue-100"><Plus size={12}/></button>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={() => addToQueue(prod, v)}
                                                                    className="px-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg hover:bg-[#51A7FD] hover:text-white hover:border-[#51A7FD] transition-all text-[8.5px] font-black uppercase tracking-widest shrink-0"
                                                                >
                                                                    Agregar
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                      )}
                                    </div>
                                  )
                              })}
                          </div>
                      )}
                  </div>
              </div>
          </div>

          {/* RIGHT: COLA E IMPRESIÓN (SIDEBAR) */}
          <div className="lg:col-span-4 flex flex-col gap-5">

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[820px]">

                  {/* Header Cola */}
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                       <span className="text-[11px] font-black uppercase tracking-widest text-[#0B2A36]">Cola de Impresión</span>
                       <span className="bg-blue-100 text-[#51A7FD] px-2.5 py-1 rounded-full text-[10px] font-black">{totalEtiquetas} ETI</span>
                  </div>

                  {/* Selector de tipo de etiqueta */}
                  <div className="px-4 pt-4">
                      <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-2xl">
                          <button
                              onClick={() => setLabelType('comercial')}
                              className={`py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 ${labelType === 'comercial' ? 'bg-white text-[#0B2A36] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                          >
                              <Tag size={12} /> Góndola
                          </button>
                          <button
                              onClick={() => setLabelType('elaboracion')}
                              className={`py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 ${labelType === 'elaboracion' ? 'bg-white text-[#0B2A36] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                          >
                              <ClipboardList size={12} /> Elaboración
                          </button>
                      </div>
                  </div>

                  {/* Settings Panel */}
                  <div className="p-4 bg-slate-50 border-b border-slate-100 mt-4 max-h-[340px] overflow-y-auto">
                       <div className="flex items-center gap-2 mb-3">
                           <Settings size={14} className="text-slate-400" />
                           <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                               {labelType === 'elaboracion' ? 'Datos de la tanda' : 'Configuración Visual'}
                           </span>
                       </div>

                       {labelType === 'comercial' ? (
                           <div className="grid grid-cols-2 gap-2 mb-3">
                               <label className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-[#51A7FD] transition-colors">
                                   <input type="checkbox" checked={configParams.mostrarNombre} onChange={(e) => setConfigParams({...configParams, mostrarNombre: e.target.checked})} className="rounded text-[#51A7FD] focus:ring-0" />
                                   <span className="text-[9px] font-bold text-slate-700 uppercase">Nombre</span>
                               </label>
                               <label className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-[#51A7FD] transition-colors">
                                   <input type="checkbox" checked={configParams.mostrarPrecio} onChange={(e) => setConfigParams({...configParams, mostrarPrecio: e.target.checked})} className="rounded text-[#51A7FD] focus:ring-0" />
                                   <span className="text-[9px] font-bold text-slate-700 uppercase">Precio</span>
                               </label>
                               <label className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-[#51A7FD] transition-colors">
                                   <input type="checkbox" checked={configParams.mostrarBarcode} onChange={(e) => setConfigParams({...configParams, mostrarBarcode: e.target.checked})} className="rounded text-[#51A7FD] focus:ring-0" />
                                   <span className="text-[9px] font-bold text-slate-700 uppercase">Barcode</span>
                               </label>
                               <label className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-[#51A7FD] transition-colors">
                                   <input type="checkbox" checked={configParams.mostrarSKU} onChange={(e) => setConfigParams({...configParams, mostrarSKU: e.target.checked})} className="rounded text-[#51A7FD] focus:ring-0" />
                                   <span className="text-[9px] font-bold text-slate-700 uppercase">REF/SKU</span>
                               </label>
                           </div>
                       ) : (
                           <div className="flex flex-col gap-2 mb-3">
                               <div className="flex flex-col">
                                   <span className="text-[8px] font-black uppercase text-slate-400 mb-1 ml-1">Fecha de elaboración</span>
                                   <input
                                       type="date"
                                       value={batchParams.fechaElaboracion}
                                       onChange={(e) => {
                                           const nuevaFecha = e.target.value;
                                           setBatchParams((prev) => ({
                                               ...prev,
                                               fechaElaboracion: nuevaFecha,
                                               // Si el lote era el autosugerido de la fecha anterior, lo re-sincronizamos:
                                               // si no, se imprime un lote cuya fecha embebida contradice la de elaboración.
                                               lote: esLoteAutogenerado(prev.lote, prev.fechaElaboracion) && nuevaFecha
                                                   ? suggestLote(nuevaFecha)
                                                   : prev.lote,
                                           }));
                                       }}
                                       className="w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] shadow-inner"
                                   />
                               </div>

                               <div className="flex flex-col">
                                   <span className="text-[8px] font-black uppercase text-slate-400 mb-1 ml-1">Número de lote</span>
                                   <div className="flex items-center gap-2">
                                       <input
                                           type="text"
                                           value={batchParams.lote}
                                           placeholder="L260726-01"
                                           onChange={(e) => setBatchParams({ ...batchParams, lote: e.target.value })}
                                           className="flex-1 min-w-0 bg-white border border-slate-200 px-3 py-2 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner"
                                       />
                                       <button
                                           onClick={() => setBatchParams({ ...batchParams, lote: bumpLoteSeq(batchParams.fechaElaboracion) })}
                                           title="Generar el siguiente número de lote del día"
                                           className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] hover:border-[#51A7FD] transition-all shrink-0"
                                       >
                                           <RefreshCcw size={12} />
                                       </button>
                                   </div>
                               </div>

                               <div className="flex flex-col">
                                   <span className="text-[8px] font-black uppercase text-slate-400 mb-1 ml-1">Elaborado por</span>
                                   <input
                                       type="text"
                                       value={batchParams.elaboradoPor}
                                       placeholder="Nombre del responsable"
                                       onChange={(e) => setBatchParams({ ...batchParams, elaboradoPor: e.target.value })}
                                       className="w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] shadow-inner"
                                   />
                               </div>

                               {/* Vencimiento */}
                               <label className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-[#51A7FD] transition-colors mt-1">
                                   <input type="checkbox" checked={batchParams.mostrarVencimiento} onChange={(e) => setBatchParams({ ...batchParams, mostrarVencimiento: e.target.checked })} className="rounded text-[#51A7FD] focus:ring-0" />
                                   <span className="text-[9px] font-bold text-slate-700 uppercase">Fecha de vencimiento</span>
                               </label>

                               {batchParams.mostrarVencimiento && (
                                   <div className="flex flex-col gap-2 pl-2 border-l-2 border-slate-200">
                                       <select
                                           value={batchParams.vencimientoModo}
                                           onChange={(e) => setBatchParams({ ...batchParams, vencimientoModo: e.target.value })}
                                           className="w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[9px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-sm appearance-none"
                                       >
                                           <option value="dias">Calcular por vida útil (días)</option>
                                           <option value="fecha">Fecha fija</option>
                                       </select>
                                       {batchParams.vencimientoModo === 'dias' ? (
                                           <div className="flex items-center gap-2">
                                               <input
                                                   type="number" min="1" max="3650"
                                                   value={batchParams.vidaUtilDias}
                                                   onChange={(e) => setBatchParams({ ...batchParams, vidaUtilDias: Math.max(1, Number(e.target.value) || 1) })}
                                                   className="w-20 bg-white border border-slate-200 px-3 py-2 rounded-xl text-[10px] font-bold text-center outline-none focus:border-[#51A7FD] shadow-inner"
                                               />
                                               <span className="text-[9px] font-black uppercase text-slate-400 tracking-widest">
                                                   Vence {formatFechaCorta(vencimientoEfectivo) || '—'}
                                               </span>
                                           </div>
                                       ) : (
                                           <input
                                               type="date"
                                               value={batchParams.fechaVencimiento}
                                               min={batchParams.fechaElaboracion}
                                               onChange={(e) => setBatchParams({ ...batchParams, fechaVencimiento: e.target.value })}
                                               className="w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] shadow-inner"
                                           />
                                       )}
                                   </div>
                               )}

                               <div className="grid grid-cols-1 gap-2">
                                   <label className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-[#51A7FD] transition-colors">
                                       <input type="checkbox" checked={batchParams.mostrarPeso} onChange={(e) => setBatchParams({ ...batchParams, mostrarPeso: e.target.checked })} className="rounded text-[#51A7FD] focus:ring-0" />
                                       <span className="text-[9px] font-bold text-slate-700 uppercase">Peso / cantidad neta</span>
                                   </label>
                                   <label className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-[#51A7FD] transition-colors">
                                       <input type="checkbox" checked={batchParams.mostrarLoteBarcode} onChange={(e) => setBatchParams({ ...batchParams, mostrarLoteBarcode: e.target.checked })} className="rounded text-[#51A7FD] focus:ring-0" />
                                       <span className="text-[9px] font-bold text-slate-700 uppercase">Código de barras del lote</span>
                                   </label>
                               </div>
                           </div>
                       )}

                       <div className="flex flex-col gap-2">
                            <select
                                value={configParams.labelSize} onChange={(e) => setConfigParams({...configParams, labelSize: e.target.value})}
                                className="w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[9px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-sm appearance-none"
                            >
                                <option value="A4">Impresora normal: hoja A4 en grilla</option>
                                <option value="ROLL">Ticketeadora / Zebra: rollo continuo</option>
                            </select>

                            {configParams.labelSize === 'A4' ? (
                               <>
                                 <div className="flex items-center gap-2">
                                     <div className="flex-1 flex flex-col">
                                         <span className="text-[8px] font-black uppercase text-slate-400 mb-1 ml-1">Ancho (cm)</span>
                                         <input
                                             type="number"
                                             step="0.1" min="1" max="20"
                                             value={configParams.a4Width}
                                             onChange={(e) => setConfigParams({...configParams, a4Width: Number(e.target.value)})}
                                             className="w-full bg-white border border-slate-200 px-3 py-1.5 rounded-xl text-[10px] font-bold text-center outline-none focus:border-[#51A7FD] shadow-inner"
                                         />
                                     </div>
                                     <div className="flex-1 flex flex-col">
                                         <span className="text-[8px] font-black uppercase text-slate-400 mb-1 ml-1">Alto (cm)</span>
                                         <input
                                             type="number"
                                             step="0.1" min="1" max="29"
                                             value={configParams.a4Height}
                                             onChange={(e) => setConfigParams({...configParams, a4Height: Number(e.target.value)})}
                                             className="w-full bg-white border border-slate-200 px-3 py-1.5 rounded-xl text-[10px] font-bold text-center outline-none focus:border-[#51A7FD] shadow-inner"
                                         />
                                     </div>
                                 </div>
                                 {labelType === 'elaboracion' && (
                                     <span className="text-[8px] font-bold uppercase tracking-widest text-slate-400 ml-1">
                                         Sugerido para elaboración: 6 × 4 cm
                                     </span>
                                 )}
                               </>
                            ) : (
                               <>
                                 <select
                                     value={ROLL_PRESETS.some(p => p.w === configParams.rollWidth && p.h === configParams.rollHeight)
                                         ? `${configParams.rollWidth}x${configParams.rollHeight}` : 'custom'}
                                     onChange={(e) => {
                                         const preset = ROLL_PRESETS.find(p => p.id === e.target.value);
                                         if (preset) setConfigParams({ ...configParams, rollWidth: preset.w, rollHeight: preset.h });
                                     }}
                                     className="w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[9px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-sm appearance-none"
                                 >
                                     {ROLL_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                                     <option value="custom" disabled>Medida personalizada (editá abajo)</option>
                                 </select>
                                 {labelType === 'elaboracion' && (
                                     <span className="text-[8px] font-bold uppercase tracking-widest text-slate-400 ml-1">
                                         La etiqueta de elaboración necesita altura: sugerido 58 × 40 mm
                                     </span>
                                 )}
                                 <div className="flex items-center gap-2">
                                     <div className="flex-1 flex flex-col">
                                         <span className="text-[8px] font-black uppercase text-slate-400 mb-1 ml-1">Ancho (mm)</span>
                                         <input
                                             type="number" step="1" min="10" max="210"
                                             value={configParams.rollWidth}
                                             onChange={(e) => setConfigParams({...configParams, rollWidth: Number(e.target.value)})}
                                             className="w-full bg-white border border-slate-200 px-3 py-1.5 rounded-xl text-[10px] font-bold text-center outline-none focus:border-[#51A7FD] shadow-inner"
                                         />
                                     </div>
                                     <div className="flex-1 flex flex-col">
                                         <span className="text-[8px] font-black uppercase text-slate-400 mb-1 ml-1">Alto (mm)</span>
                                         <input
                                             type="number" step="1" min="10" max="297"
                                             value={configParams.rollHeight}
                                             onChange={(e) => setConfigParams({...configParams, rollHeight: Number(e.target.value)})}
                                             className="w-full bg-white border border-slate-200 px-3 py-1.5 rounded-xl text-[10px] font-bold text-center outline-none focus:border-[#51A7FD] shadow-inner"
                                         />
                                     </div>
                                 </div>
                               </>
                            )}

                            <select
                                value={configParams.fontSize} onChange={(e) => setConfigParams({...configParams, fontSize: e.target.value})}
                                className="w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[9px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-sm appearance-none"
                            >
                                <option value="normal">Tamaño de Letra: Normal</option>
                                <option value="small">Tamaño de Letra: Pequeño</option>
                                <option value="large">Tamaño de Letra: Grande</option>
                            </select>
                       </div>
                  </div>

                  {/* Listado de Cola */}
                  <div className="flex-1 overflow-y-auto bg-white p-3">
                      {queueItems.length === 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-3 border-2 border-dashed border-slate-100 rounded-2xl p-4">
                              <Tag size={28} />
                              <span className="text-[9px] font-black uppercase tracking-widest text-[#0B2A36] text-center opacity-40">Agregá productos a la cola</span>
                          </div>
                      ) : (
                          <div className="flex flex-col gap-2">
                              {queueItems.map(item => {
                                  const { name, barcode } = resolveLabelData(item);
                                  const sinBarcode = !barcode;
                                  return (
                                  <div key={item.key} className="flex flex-col border border-slate-200 rounded-xl p-3 bg-white relative group shadow-sm hover:border-[#51A7FD]/50 transition-colors">
                                      <button onClick={() => removeFromQueue(item.key)} className="absolute top-2.5 right-2.5 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <Trash2 size={12} />
                                      </button>
                                      <span className="text-[10px] font-black text-slate-800 uppercase leading-tight pr-6 mb-2 line-clamp-2">{name}</span>

                                      {labelType === 'comercial' && configParams.mostrarBarcode && sinBarcode && (
                                          <span className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-amber-600 mb-2">
                                              <AlertTriangle size={10} /> Sin código de barras
                                          </span>
                                      )}

                                      {labelType === 'elaboracion' && batchParams.mostrarPeso && (
                                          <input
                                              type="text"
                                              value={item.peso || ''}
                                              placeholder="Peso / cant. neta (ej. 500 g)"
                                              onChange={(e) => updateQueuePeso(item.key, e.target.value)}
                                              className="w-full bg-slate-50 border border-slate-200 px-2.5 py-1.5 rounded-lg text-[9px] font-bold outline-none focus:border-[#51A7FD] mb-2"
                                          />
                                      )}

                                      <div className="flex items-center justify-between mt-auto">
                                          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-0.5 shadow-sm">
                                              <button onClick={() => updateQueueQty(item.key, item.qty - 1)} className="w-5 h-5 flex items-center justify-center bg-white rounded shadow-sm text-slate-600 hover:text-rose-500"><Minus size={10}/></button>
                                              <input
                                                  type="number"
                                                  min="1" max="999"
                                                  value={qtyDrafts[item.key] ?? item.qty}
                                                  onChange={(e) => handleQtyInput(item.key, e.target.value)}
                                                  onBlur={() => commitQtyDraft(item.key)}
                                                  className="w-10 text-center text-[10px] font-black text-[#0B2A36] bg-transparent outline-none border-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                              />
                                              <button onClick={() => updateQueueQty(item.key, item.qty + 1)} className="w-5 h-5 flex items-center justify-center bg-white rounded shadow-sm text-slate-600 hover:text-[#51A7FD]"><Plus size={10}/></button>
                                          </div>
                                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{item.qty} etiquetas</span>
                                      </div>
                                  </div>
                                  );
                              })}
                          </div>
                      )}
                  </div>

                  {/* Action Footer */}
                  <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
                       {labelType === 'comercial' && configParams.mostrarBarcode && itemsSinBarcode.length > 0 && (
                           <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl">
                               <AlertTriangle size={12} className="text-amber-500 shrink-0" />
                               <span className="text-[8.5px] font-black uppercase tracking-widest text-amber-700 leading-snug">
                                   {itemsSinBarcode.length} ítem(s) sin código de barras: van a imprimirse sin el código
                               </span>
                           </div>
                       )}
                       <button
                          disabled={totalEtiquetas === 0}
                          onClick={handlePrint}
                          className="w-full py-3 bg-[#0B2A36] text-[#3FF2AA] rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                       >
                           <Printer size={16} /> Procesar {totalEtiquetas} ETI
                       </button>
                       {totalEtiquetas > 0 && (
                           <button onClick={clearQueue} className="w-full py-2.5 mt-2 bg-white text-rose-500 border border-rose-100 hover:bg-rose-50 rounded-xl transition-all text-[9.5px] font-black uppercase tracking-widest shadow-sm">
                               Vaciar Lista de Espera
                           </button>
                       )}
                  </div>
              </div>
          </div>
      </div>

      {/* MODAL PRINT IFRAME */}
      {printModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl h-[85vh] rounded-3xl overflow-hidden flex flex-col shadow-2xl animate-in slide-in-from-bottom-4 zoom-in-95 duration-200">
            <div className="bg-[#0B2A36] p-5 flex justify-between items-center text-white">
              <div className="flex items-center gap-3">
                <Printer size={20} className="text-[#3FF2AA]"/>
                <span className="font-black text-[10px] uppercase tracking-[0.2em]">Visor de Etiquetas</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { iframeRef.current?.contentWindow?.print(); }} className="bg-[#3FF2AA] text-[#0B2A36] px-6 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg hover:brightness-110 active:scale-95 transition-all flex items-center gap-2">
                    <Printer size={14} /> Imprimir Ahora
                </button>
                <button onClick={()=>setPrintModalOpen(false)} className="bg-white/10 p-2.5 rounded-xl hover:bg-white/20 transition-all"><X size={20}/></button>
              </div>
            </div>

            {/* Disclaimer para A4/Rollo */}
            <div className="bg-blue-50/50 border-b border-blue-100 px-5 py-3 flex items-center gap-3">
                <Layers size={18} className="text-blue-500" />
                <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wide leading-relaxed">
                    Etiqueta: <strong className="font-black text-blue-900">{labelType === 'elaboracion' ? 'Elaboración' : 'Góndola'}</strong> · Formato: <strong className="font-black text-blue-900">{formatoActual}</strong><br/>
                    Asegurate de configurar en el recuadro de impresión: <strong className="font-black text-blue-900">Márgenes en 'Ninguno', Escala 100%, Deshabilitar "Headers y Footers"</strong>.
                </span>
            </div>

            <iframe title="Previsualización Etiquetas" ref={iframeRef} srcDoc={printHtml} className="flex-1 w-full bg-slate-200 border-none relative"/>

            {/* Loading iframe state overlay opcional */}
            {!printHtml && (
                <div className="absolute inset-0 z-10 bg-white/80 flex items-center justify-center pt-20">
                    <RefreshCcw className="animate-spin text-slate-300" size={32} />
                </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
